import type { MenuItem } from '@/consts/navigation';

/**
 * Recursively removes items marked `hidden`, and drops empty containers
 * left after their visible children were filtered out.
 *
 * - Items without children (no field, or empty array) are leaves and kept
 *   unless hidden — preserves Favorites/Recents during loading when
 *   `buildDynamicSection` sets `children: []`.
 * - A pure container (no `to` route) with zero visible children is dropped
 *   (covers AC 4 & 5: empty Groups/Parents auto-hide).
 * - A navigable parent (has its own `to` route) with zero visible children
 *   is kept as a leaf — its route remains reachable from the menu.
 */
export const filterHiddenItems = (items: MenuItem[]): MenuItem[] => {
  return items.reduce<MenuItem[]>((acc, item) => {
    if (item.hidden) return acc;
    if (item.children === undefined || item.children.length === 0) {
      acc.push(item);
      return acc;
    }
    const visibleChildren = filterHiddenItems(item.children);
    if (visibleChildren.length === 0) {
      if (item.to === undefined) return acc;
      const { children: _empty, ...leafified } = item;
      acc.push(leafified);
      return acc;
    }
    acc.push({ ...item, children: visibleChildren });
    return acc;
  }, []);
};
